import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGoogleMapsScript } from '../../hooks/useGoogleMapsScript';

const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-indigo-500/30';

export default function GoogleAddressAutocomplete({
    value,
    onChange,
    onPlaceSelected,
    disabled = false,
    id = 'address-search',
}) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const autocompleteRef = useRef(null);
    const { ready, error } = useGoogleMapsScript();

    useEffect(() => {
        if (!ready || !inputRef.current || disabled) return undefined;
        if (!window.google?.maps?.places) return undefined;

        const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
            types: ['address'],
            componentRestrictions: { country: ['it'] },
            fields: ['address_components', 'formatted_address', 'place_id'],
        });

        autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (!place) return;
            onPlaceSelected?.(place);
        });

        autocompleteRef.current = autocomplete;

        return () => {
            if (autocompleteRef.current && window.google?.maps?.event) {
                window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
            }
            autocompleteRef.current = null;
        };
    }, [ready, disabled, onPlaceSelected]);

    if (error === 'missing_key') {
        return (
            <div>
                <input
                    type="text"
                    id={id}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    disabled={disabled}
                    className={inputClass}
                    placeholder={t('gym.editExtendedProfile.placeholders.addressManual')}
                />
                <p className="mt-1 text-xs text-amber-600 dark:text-amber-300">
                    {t('gym.editExtendedProfile.hints.googleMissingKey')}
                </p>
            </div>
        );
    }

    return (
        <div>
            <input
                ref={inputRef}
                type="text"
                id={id}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                disabled={disabled || !ready}
                className={inputClass}
                placeholder={t('gym.editExtendedProfile.placeholders.addressSearch')}
                autoComplete="off"
            />
            {!ready && !error && (
                <p className="mt-1 text-xs text-gray-500 dark:text-white/50">
                    {t('gym.editExtendedProfile.hints.googleLoading')}
                </p>
            )}
            {error === 'load_failed' && (
                <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {t('gym.editExtendedProfile.hints.googleLoadFailed')}
                </p>
            )}
        </div>
    );
}
